/**
 * Tableaux blancs (2026-11-02) : un tableau blanc ou un paperboard posé sur
 * une carte s'ouvre dans Excalidraw (« Ouvrir le tableau »). Le dessin est à
 * plusieurs en direct (relais par la salle temps réel) et conservé en base
 * (table `whiteboards`, un tableau par objet posé).
 * Sous-chemin `@campus/core/whiteboards` côté serveur, jamais le barrel.
 */

/** Objets du catalogue qui s'ouvrent comme tableau (pas les tableaux décoratifs ni les vues de dos). */
export const WHITEBOARD_FURNITURE: ReadonlySet<string> = new Set([
  'pl_paperboard', 'pl_paperboard_notes', 'pl_paperboard_graphique', 'pl_paperboard_postits',
  'ch_mb-tableau-blanc', 'ch_cc-tableau-blanc', 'ch_at-tableau-blanc',
  'ch_paperboard', 'ch_tableau', 'ch_etude-panneau',
  'lz_library_36', 'lz_library_39',
]);

export function isWhiteboard(furnitureId: string): boolean {
  return WHITEBOARD_FURNITURE.has(furnitureId);
}

/** Éléments au plus par tableau (supprimés compris) et par envoi. */
export const WHITEBOARD_MAX_ELEMENTS = 5000;
export const WHITEBOARD_MAX_UPDATE = 500;
/** Taille d'un envoi enregistré (JSON), en octets. */
export const WHITEBOARD_MAX_BYTES = 2_000_000;

/** Élément Excalidraw, tel que gardé et fusionné (le reste est opaque). */
export interface WhiteboardElement {
  id: string;
  version: number;
  versionNonce: number;
  isDeleted?: boolean;
  [key: string]: unknown;
}

/** Garde les éléments bien formés (id, version, versionNonce) ; null si la liste n'en est pas une. */
export function sanitizeWhiteboardElements(raw: unknown, max = WHITEBOARD_MAX_ELEMENTS): WhiteboardElement[] | null {
  if (!Array.isArray(raw) || raw.length > max) return null;
  const out: WhiteboardElement[] = [];
  for (const e of raw) {
    if (!e || typeof e !== 'object') return null;
    const el = e as Record<string, unknown>;
    if (typeof el.id !== 'string' || el.id.length === 0 || el.id.length > 64) return null;
    if (typeof el.version !== 'number' || typeof el.versionNonce !== 'number') return null;
    // Images : jamais (les fichiers ne sont ni envoyés ni gardés).
    if (el.type === 'image') continue;
    out.push(el as WhiteboardElement);
  }
  return out;
}

/** L'élément reçu remplace-t-il celui qu'on a ? Même règle qu'Excalidraw (version, puis plus petit versionNonce). */
export function isNewerElement(incoming: WhiteboardElement, current: WhiteboardElement | undefined): boolean {
  if (!current) return true;
  if (incoming.version !== current.version) return incoming.version > current.version;
  return incoming.versionNonce < current.versionNonce;
}

/** Fusionne des éléments reçus dans ceux du tableau, élément par élément ; l'ordre des existants est gardé. */
export function mergeWhiteboardElements(current: WhiteboardElement[], incoming: WhiteboardElement[]): WhiteboardElement[] {
  const byId = new Map(current.map((e) => [e.id, e] as const));
  const order = current.map((e) => e.id);
  for (const e of incoming) {
    if (!byId.has(e.id)) order.push(e.id);
    if (isNewerElement(e, byId.get(e.id))) byId.set(e.id, e);
  }
  return order.map((id) => byId.get(id)!);
}
